import type { ModalitaPartecipazione } from '@aulaviva/shared';
import { Button, Dialog, cx } from '@aulaviva/ui';
import { Building2, Wifi } from 'lucide-react';
import { useState } from 'react';
import { percorsi } from '@/testi';

export interface DialogoModalitaProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attuale?: ModalitaPartecipazione | null;
  onConferma: (modalita: ModalitaPartecipazione) => Promise<unknown>;
}

/** Scelta online / in sala: due grandi aree selezionabili con radio visibile. */
export function DialogoModalita({ open, onOpenChange, attuale, onConferma }: DialogoModalitaProps) {
  const t = percorsi.dialogo;
  const [scelta, setScelta] = useState<ModalitaPartecipazione>(attuale ?? 'online');
  const [inCorso, setInCorso] = useState(false);

  async function conferma() {
    setInCorso(true);
    try {
      await onConferma(scelta);
      onOpenChange(false);
    } finally {
      setInCorso(false);
    }
  }

  const opzioni: Array<{ v: ModalitaPartecipazione; titolo: string; testo: string; icona: typeof Wifi }> = [
    { v: 'online', titolo: t.online, testo: t.onlineTesto, icona: Wifi },
    { v: 'sala', titolo: t.sala, testo: t.salaTesto, icona: Building2 },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.titolo}
      description={t.descrizione}
      actions={
        <>
          <Button onClick={() => onOpenChange(false)}>{t.annulla}</Button>
          <Button variant="primary" onClick={conferma} loading={inCorso} loadingText={t.inCorso}>
            {percorsi.azioni.conferma}
          </Button>
        </>
      }
    >
      <div role="radiogroup" aria-label={t.titolo} className="flex flex-col gap-3">
        {opzioni.map(({ v, titolo, testo, icona: Icona }) => {
          const attiva = scelta === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={attiva}
              onClick={() => setScelta(v)}
              className={cx(
                'flex items-start gap-4 text-left rounded-md border p-4 min-h-[44px] bg-surface',
                attiva ? 'border-accent ring-2 ring-accent' : 'border-border-strong hover:bg-surface-sunken',
              )}
            >
              <span
                aria-hidden
                className={cx('mt-1 h-5 w-5 shrink-0 rounded-full border-2 inline-flex items-center justify-center', attiva ? 'border-accent' : 'border-border-strong')}
              >
                {attiva && <span className="h-2.5 w-2.5 rounded-full bg-accent" />}
              </span>
              <span className="flex flex-col gap-1">
                <span className="t-title-3 inline-flex items-center gap-2">
                  <Icona aria-hidden size={20} strokeWidth={1.75} />
                  {titolo}
                </span>
                <span className="t-body-small text-ink-muted">{testo}</span>
              </span>
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}
